import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import type {
  FinanceSummaryData,
  KpiMetric,
  ActionItem,
  RevenueByIsp,
  PnlWeek,
} from './use-finance-summary.js';

function fmt(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function fmtWeek(w: string): string {
  if (!w) return '';
  const d = new Date(w + 'T12:00:00');
  if (isNaN(d.getTime())) return w;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const SEVERITY_STYLE: Record<string, { color: string; bg: string; label: string }> = {
  flagged: { color: C.red, bg: 'rgba(255,107,107,0.12)', label: 'FLAGGED' },
  caution: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)', label: 'CAUTION' },
  info:    { color: C.textMuted, bg: 'rgba(255,255,255,0.05)', label: 'INFO' },
};

function KpiCard({ label, metric, prefix }: { label: string; metric: KpiMetric; prefix?: string }) {
  const deltaColor = metric.delta_pct > 0 ? C.green : metric.delta_pct < 0 ? C.red : C.textMuted;
  return (
    <div style={{ padding: 16, borderRadius: 12, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
      <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: C.text, marginBottom: 4 }}>
        {prefix}{fmt(metric.total)}
      </div>
      <div style={{ fontSize: 12, color: deltaColor }}>
        {fmtPct(metric.delta_pct)} vs prior
      </div>
      {metric.sparkline.length > 0 && (
        <div style={{ display: 'flex', gap: 2, marginTop: 8, alignItems: 'flex-end', height: 24 }}>
          {metric.sparkline.map((pt, i) => {
            const max = Math.max(...metric.sparkline.map(p => Math.abs(p.value)), 1);
            const h = Math.max(2, (Math.abs(pt.value) / max) * 24);
            return <div key={i} style={{ flex: 1, height: h, background: pt.value >= 0 ? C.green : C.red, borderRadius: 2, opacity: 0.7 }} />;
          })}
        </div>
      )}
    </div>
  );
}

function ActionItemRow({ item }: { item: ActionItem }) {
  const style = SEVERITY_STYLE[item.severity] || SEVERITY_STYLE.info;
  return (
    <div style={{ padding: 12, borderRadius: 10, background: style.bg, border: `1px solid ${style.color}30`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, color: style.color, background: `${style.color}20`, whiteSpace: 'nowrap' }}>
        {style.label}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{item.text}</div>
        {item.dollar_impact != null && item.dollar_impact !== 0 && (
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Impact: {fmt(item.dollar_impact)}</div>
        )}
      </div>
    </div>
  );
}

function IspRow({ isp }: { isp: RevenueByIsp }) {
  return (
    <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
          ISP {isp.isp_number}{isp.terminal ? ` \u00b7 ${isp.terminal}` : ''}
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
          <span>Revenue: {fmt(isp.net_settlement)}</span>
          <span>Stops: {isp.stops.toLocaleString()}</span>
          <span>$/stop: ${isp.dollar_per_stop.toFixed(2)}</span>
          {isp.chargebacks !== 0 && <span style={{ color: C.red }}>Chargebacks: {fmt(isp.chargebacks)}</span>}
        </div>
      </div>
    </div>
  );
}

interface FinanceSummaryDashboardProps {
  data: FinanceSummaryData;
  onRefresh: () => void;
}

export default function FinanceSummaryDashboard({ data, onRefresh }: FinanceSummaryDashboardProps) {
  const { kpis, revenue_by_isp, pnl_weekly, action_items, period } = data;

  const pnlChartData = pnl_weekly.map((w: PnlWeek) => ({
    week: fmtWeek(w.week_ending),
    Revenue: w.revenue,
    Expenses: w.expenses,
    Net: w.net,
  }));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Period + refresh */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: C.textMuted }}>
          {period.weeks_included} week{period.weeks_included !== 1 ? 's' : ''} included
          {period.start_date && ` \u00b7 ${period.start_date} \u2013 ${period.end_date}`}
        </div>
        <button
          onClick={onRefresh}
          style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <KpiCard label="Revenue" metric={kpis.revenue} />
        <KpiCard label="Expenses" metric={kpis.expenses} />
        <KpiCard label="Net Income" metric={kpis.net_income} />
      </div>

      {/* Secondary KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        {[
          { label: 'Margin', value: `${kpis.margin_pct}%`, color: kpis.margin_pct > 0 ? C.green : C.red },
          { label: '$/Stop', value: `$${kpis.dollar_per_stop.toFixed(2)}`, color: C.gold },
          { label: 'Labor/Stop', value: `$${kpis.labor_per_stop.toFixed(2)}`, color: C.text },
          { label: 'Total Stops', value: kpis.total_stops.toLocaleString(), color: C.text },
          { label: 'Chargebacks', value: fmt(kpis.chargebacks), color: kpis.chargebacks > 0 ? C.red : C.textMuted },
          { label: 'Fuel Recovery', value: `${kpis.fuel_recovery_pct}%`, color: kpis.fuel_recovery_pct > 80 ? C.green : C.red },
        ].map((s) => (
          <div key={s.label} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Action items */}
      {action_items.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Action items ({action_items.length})
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {action_items.map((item, i) => (
              <ActionItemRow key={i} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Weekly P&L chart */}
      {pnlChartData.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
            Weekly P&L
          </div>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pnlChartData}>
                <CartesianGrid stroke={C.borderSoft} vertical={false} />
                <XAxis dataKey="week" stroke={C.textMuted} tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke={C.textMuted} tickLine={false} axisLine={false} fontSize={11} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: C.surface, border: `1px solid ${C.borderSoft}`, borderRadius: 8, fontSize: 12 }}
                  formatter={(value: number) => fmt(value)}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Revenue" fill={C.green} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expenses" fill={C.red} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Net" fill={C.gold} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Revenue by ISP */}
      {revenue_by_isp.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Revenue by ISP
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {revenue_by_isp.map((isp) => (
              <IspRow key={isp.isp_number} isp={isp} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
